import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { requestAPI, requestTypeAPI, deviceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const RequestForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isIssue = searchParams.get('type') === 'issue';

  const [loading, setLoading] = useState(false);
  const [requestTypes, setRequestTypes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [formData, setFormData] = useState({
    requestTypeId: '',
    deviceId: '',
    title: '',
    description: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchRequestTypes();
    fetchDevices();
  }, []);

  const fetchRequestTypes = async () => {
    try {
      const response = await requestTypeAPI.getAll({
        isActive: true,
        category: isIssue ? 'issue' : 'request'
      });
      setRequestTypes(response.data);
    } catch (error) {
      toast.error('Talep türleri yüklenemedi');
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await deviceAPI.getAll({ assignedUserId: user.id });
      setDevices(response.data);
    } catch (error) {
      console.error('Devices fetch error:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = { ...formData };
      if (!data.deviceId) delete data.deviceId;

      const response = await requestAPI.create(data);
      toast.success('Talep oluşturuldu');

      if (response.data.whatsappLinks?.length > 0) {
        setWhatsappLinks(response.data.whatsappLinks);
      } else {
        navigate('/requests');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Talep oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  if (whatsappLinks.length > 0) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-green-500 mb-4">
            <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Talep Oluşturuldu!</h2>
          <p className="text-gray-600 mb-6">IT ekibine email ile bildirildi.</p>

          <div className="border-t pt-4">
            <p className="text-sm text-gray-500 mb-4">Acil durumlar için WhatsApp ile iletişime geçebilirsiniz:</p>
            <div className="space-y-2">
              {whatsappLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {link.user} ile WhatsApp
                </a>
              ))}
            </div>
          </div>

          <button
            onClick={() => navigate('/requests')}
            className="mt-6 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Taleplerime Git
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/requests')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isIssue ? 'Arıza Bildir' : 'Yeni Talep Oluştur'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {isIssue ? 'Arıza Türü' : 'Talep Türü'} *
            </label>
            <select
              name="requestTypeId"
              required
              value={formData.requestTypeId}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Seçiniz</option>
              {requestTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          {devices.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700">İlgili Cihaz</label>
              <select
                name="deviceId"
                value={formData.deviceId}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Cihaz seçiniz (opsiyonel)</option>
                {devices.map(device => (
                  <option key={device.id} value={device.id}>
                    {device.name} - {device.brand} {device.model}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Başlık *</label>
            <input
              type="text"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={isIssue ? 'Arıza başlığı' : 'Talep başlığı'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Açıklama *</label>
            <textarea
              name="description"
              required
              value={formData.description}
              onChange={handleChange}
              rows="5"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={isIssue ? 'Arızayı detaylı olarak açıklayınız...' : 'Talebinizi detaylı olarak açıklayınız...'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Öncelik</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="low">Düşük</option>
              <option value="medium">Orta</option>
              <option value="high">Yüksek</option>
              <option value="critical">Kritik</option>
            </select>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Talep Eden Bilgileri</h3>
            <p className="text-sm text-gray-600">{user.fullName}</p>
            <p className="text-sm text-gray-500">{user.department} - {user.location}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/requests')}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${
              isIssue ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Gönderiliyor...' : (isIssue ? 'Arıza Bildir' : 'Talep Gönder')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequestForm;
